import { Intl, Temporal, toTemporalInstant as toTemporalInstantPolyfill } from '@js-temporal/polyfill';
import { z } from 'zod';

// This central reexport allows us to swap polyfills if we have any reason to
// (there are a couple out there in the wild)
export { Temporal, Intl };

export const BEGINNING_OF_DAY = Temporal.PlainTime.from('00:00');
export const END_OF_DAY = BEGINNING_OF_DAY.subtract({ microseconds: 1 });

export const zodTemporalInstant = z
  .instanceof(Temporal.Instant)
  .or(z.date())
  .or(z.string())
  .transform((d) =>
    d instanceof Temporal.Instant
      ? d
      : d instanceof Date
      ? Temporal.Instant.from(d.toISOString())
      : Temporal.Instant.from(d),
  );

export const zodTemporalPlainDate = z
  .instanceof(Temporal.PlainDate)
  .or(z.date())
  .or(z.string())
  .transform((d) =>
    d instanceof Temporal.PlainDate
      ? d
      : d instanceof Date
      ? Temporal.PlainDate.from(d.toISOString().split('T')[0])
      : Temporal.PlainDate.from(d),
  );

export const zodTemporalPlainTime = z
  .instanceof(Temporal.PlainTime)
  .or(z.string().transform((s) => Temporal.PlainTime.from(s)));

export const zodTemporalZonedDateTime = z
  .instanceof(Temporal.ZonedDateTime)
  .or(z.string())
  .pipe(z.transform((s) => Temporal.ZonedDateTime.from(s)))
  .meta({
    type: 'string',
    example: '2025-07-08T22:32:15.699935523+00:00[UTC]',
    description: 'An ISO-8601 timestamp with a timezone extension (RFC 9557 format).',
  });

export const zodTemporalTimeZone = z
  .instanceof(Temporal.TimeZone)
  .or(z.string().transform((s) => Temporal.TimeZone.from(s)));

export const zodTemporalDuration = z
  .instanceof(Temporal.Duration)
  .or(z.string().transform((s) => Temporal.Duration.from(s)));

/**
 * Converts certain Temporal objects to legacy JavaScript Date objects:
 * - Temporal.Instant is converted directly to a Date object representing the same instant in time.
 * - Temporal.PlainDate is converted to a Date object representing 00:00 UTC time on the PlainDate
 */
export function toLegacyDate(t: Temporal.Instant | Temporal.PlainDate): Date {
  const instant = t instanceof Temporal.Instant ? t : t.toZonedDateTime('UTC').toInstant();
  return new Date(instant.epochMilliseconds);
}

/**
 * Wrapper around the `toTemporalInstant` polyfill that allows us to use it as a function.
 *
 * @todo deprecate and remove this when native Date.prototype.toTemporalInstant lands
 */
export const toTemporalInstant = (d: Date) => toTemporalInstantPolyfill.apply(d);

/**
 * @description Checks if the first instant or zoned date time is after the second instant or zoned date time.
 * @param dateA - The first instant or zoned date time to compare.
 * @param dateB - The second instant or zoned date time to compare.
 */
export function isAfter(dateA: Temporal.PlainDate, dateB: Temporal.PlainDate): boolean;
export function isAfter(
  dateA: Temporal.Instant | Temporal.ZonedDateTime,
  dateB: Temporal.Instant | Temporal.ZonedDateTime,
): boolean;
export function isAfter(
  dateA: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
  dateB: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
): boolean {
  if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(dateA, dateB) > 0;
  }
  if (dateA instanceof Temporal.PlainDate || dateB instanceof Temporal.PlainDate) {
    throw new Error('Cannot compare PlainDate with Instant or ZonedDateTime');
  }

  const dateAInstant = dateA instanceof Temporal.Instant ? dateA : dateA.toInstant();
  const dateBInstant = dateB instanceof Temporal.Instant ? dateB : dateB.toInstant();
  return dateAInstant.epochMilliseconds > dateBInstant.epochMilliseconds;
}

/**
 * @description Checks if the first instant or zoned date time is after or equal to the second instant or zoned date time.
 * @param dateA - The first instant or zoned date time to compare.
 * @param dateB - The second instant or zoned date time to compare.
 * @returns Whether the first instant or zoned date time is after or equal to the second instant or zoned date time.
 */
export function isAfterOrEqualTo(dateA: Temporal.PlainDate, dateB: Temporal.PlainDate): boolean;
export function isAfterOrEqualTo(
  dateA: Temporal.Instant | Temporal.ZonedDateTime,
  dateB: Temporal.Instant | Temporal.ZonedDateTime,
): boolean;
export function isAfterOrEqualTo(
  dateA: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
  dateB: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
): boolean {
  if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(dateA, dateB) >= 0;
  }
  if (dateA instanceof Temporal.PlainDate || dateB instanceof Temporal.PlainDate) {
    throw new Error('Cannot compare PlainDate with Instant or ZonedDateTime');
  }

  const dateAInstant = dateA instanceof Temporal.Instant ? dateA : dateA.toInstant();
  const dateBInstant = dateB instanceof Temporal.Instant ? dateB : dateB.toInstant();
  return dateAInstant.epochMilliseconds >= dateBInstant.epochMilliseconds;
}

/**
 * @description Checks if the first instant or zoned date time is before the second instant or zoned date time.
 * @param dateA - The first instant or zoned date time to compare.
 * @param dateB - The second instant or zoned date time to compare.
 */
export function isBefore(dateA: Temporal.PlainDate, dateB: Temporal.PlainDate): boolean;
export function isBefore(
  dateA: Temporal.Instant | Temporal.ZonedDateTime,
  dateB: Temporal.Instant | Temporal.ZonedDateTime,
): boolean;
export function isBefore(
  dateA: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
  dateB: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
): boolean {
  if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(dateA, dateB) < 0;
  }
  if (dateA instanceof Temporal.PlainDate || dateB instanceof Temporal.PlainDate) {
    throw new Error('Cannot compare PlainDate with Instant or ZonedDateTime');
  }

  const dateAInstant = dateA instanceof Temporal.Instant ? dateA : dateA.toInstant();
  const dateBInstant = dateB instanceof Temporal.Instant ? dateB : dateB.toInstant();
  return dateAInstant.epochMilliseconds < dateBInstant.epochMilliseconds;
}

/**
 *
 * @param dateA - The first instant or zoned date time to compare.
 * @param dateB - The second instant or zoned date time to compare.
 * @returns Whether the first instant or zoned date time is before or equal to the second instant or zoned date time.
 */
export function isBeforeOrEqualTo(dateA: Temporal.PlainDate, dateB: Temporal.PlainDate): boolean;
export function isBeforeOrEqualTo(
  dateA: Temporal.Instant | Temporal.ZonedDateTime,
  dateB: Temporal.Instant | Temporal.ZonedDateTime,
): boolean;
export function isBeforeOrEqualTo(
  dateA: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
  dateB: Temporal.Instant | Temporal.ZonedDateTime | Temporal.PlainDate,
): boolean {
  if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(dateA, dateB) <= 0;
  }
  if (dateA instanceof Temporal.PlainDate || dateB instanceof Temporal.PlainDate) {
    throw new Error('Cannot compare PlainDate with Instant or ZonedDateTime');
  }

  const dateAInstant = dateA instanceof Temporal.Instant ? dateA : dateA.toInstant();
  const dateBInstant = dateB instanceof Temporal.Instant ? dateB : dateB.toInstant();
  return dateAInstant.epochMilliseconds <= dateBInstant.epochMilliseconds;
}
/**
 * Converts a {@link Date} object with no time component to a {@link Temporal.PlainDate} object.
 *
 * @throws {Error} if the Date object has a time component other than 00:00:00 UTC
 */
export function jsPlainDateToTemporalPlainDate(date: Date): Temporal.PlainDate {
  const zdt = toTemporalInstant(date).toZonedDateTimeISO('UTC');
  const zdtPlainTime = zdt.toPlainTime();
  if (!zdtPlainTime.equals(BEGINNING_OF_DAY)) {
    throw new Error(`jsPlainDateToTemporalPlainDate only supports Dates with a 00:00:00 UTC time, got ${zdtPlainTime}`);
  }
  return zdt.toPlainDate();
}

/**
 * Converts a {@link Date} object with a time component to a
 * {@link Temporal.PlainDate} object.
 *
 * Because a particular point in time may resolve to different dates in
 * different time zones, you must provide the time zone you wish to use for the
 * computation.
 *
 * If you want to convert a plain date stored as a Date object, @see {@link jsPlainDateToTemporalPlainDate} instead.
 */
export function jsDateTimeToTemporalPlainDate(d: Date, timeZone: Temporal.TimeZoneLike): Temporal.PlainDate {
  const zdt = toTemporalInstant(d).toZonedDateTimeISO(timeZone);
  return zdt.toPlainDate();
}
